import { useCallback, useState } from 'react';

/*
    Overrides for the few fields no input owns: the image, the locale pack, and the timestamps a
    save returns. They sit on top of the loaded record, so the save still sends one whole record.

    `recordDirty` exists because react-hook-form's isDirty cannot see them, and the unsaved-changes
    warning would otherwise let them go without a word.
*/
export function useEditorRecord<Item extends object>(loaded: Item | undefined) {
    const [overrides, setOverrides] = useState<Partial<Item>>({});
    const [dirty, setDirty] = useState(false);

    const record = loaded ? ({ ...loaded, ...overrides } as Item) : undefined;

    /*
        An edit the form cannot hold. It counts towards unsaved changes.

        Pass `dirties: false` for something the server has already stored — the SEO metadata saves on
        its own button, and marking the page dirty after that save asks the admin to confirm leaving
        a page with nothing left to save. It does not clear the flag either, because another
        unsaved change may still be outstanding.
    */
    const applyEdit = useCallback((change: Partial<Item>, { dirties = true } = {}) => {
        setOverrides(current => ({ ...current, ...change }));
        if (dirties) setDirty(true);
    }, []);

    // What came back from a successful save. It is the new baseline, not an edit.
    const applySaved = useCallback((saved: Item) => {
        setOverrides(current => ({ ...current, ...saved }));
        setDirty(false);
    }, []);

    return { record, recordDirty: dirty, applyEdit, applySaved };
}
